import React, { useState, Children } from 'react';
import { SEO } from '../components/seo/SEO';
import { BreadcrumbSchema } from '../components/seo/Schema';
import { ResultsSummary } from '../components/sections/ResultsSummary';
import { VideoTestimonials } from '../components/sections/VideoTestimonials';
import { ClientLogos } from '../components/sections/ClientLogos';
import { DetailedCaseStudyCard } from '../components/ui/DetailedCaseStudyCard';
import { PDFModal } from '../components/ui/PDFModal';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export function CaseStudies() {
  const [currentSet, setCurrentSet] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState(0);
  const [selectedPDF, setSelectedPDF] = useState<{
    url: string;
    title: string;
  } | null>(null);
  // Single PDF URL for all case studies
  const CASE_STUDIES_PDF = 'https://drive.google.com/file/d/1ZqKzYV30ugwdJwVzLh7Lja_6_teF_6kS/preview';
  const caseStudies = [{
    industry: 'Life Insurance',
    companyProfile: 'Multi-Market Senior Benefits Organization',
    challenge: 'No consistent candidate pipeline, unpredictable hiring volume, no scalable system for expansion, leadership stretched thin trying to recruit while running the business.',
    solution: 'Built and implemented a structured recruiting system designed for duplication across markets, including campaign strategy, market rollout sequencing, funnel optimization, and data tracking.',
    results: [{
      label: 'Final Interviews',
      value: 144,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 101,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 70.1,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Windows',
    companyProfile: '4 offices, 50+ reps, Western U.S.',
    challenge: 'Expansion limited by recruiting capacity. Managers were split between sales, hiring, and training. Entering new markets felt risky without consistent support.',
    solution: 'Installed a scalable recruiting and leadership support framework including multi-market campaign launches, ongoing coaching, and strategic expansion planning.',
    results: [{
      label: 'Final Interviews',
      value: 388,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 250,
      suffix: '+',
      icon: 'users' as const
    }, {
      label: 'New Revenue',
      value: 2,
      prefix: '$',
      suffix: 'M+',
      icon: 'dollar' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Windows',
    companyProfile: 'Rapid growth in competitive Western markets',
    challenge: 'Growth was outpacing internal recruiting capacity. Competition increased cost-per-hire and reduced applicant quality.',
    solution: 'Deployed a structured, market-specific recruiting system with consistent optimization, predictable hiring volume, and strategic guidance for expansion pacing.',
    results: [{
      label: 'Final Interviews',
      value: 159,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 93,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 58.5,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Telecom',
    companyProfile: 'National provider focused in California',
    challenge: 'Intense regional competition for talent, rising recruiting costs, low-quality pipelines from traditional methods.',
    solution: 'Launched a tailored, region-specific recruiting system with optimized targeting, refined messaging, and tighter screening processes.',
    results: [{
      label: 'Final Interviews',
      value: 48,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 43,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 89.6,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Roofing',
    companyProfile: 'Atlanta-based company targeting aggressive growth',
    challenge: 'Limited internal capacity to recruit at speed. Lack of consistent cost-per-hire benchmarks. Leadership stretched thin between recruiting and training.',
    solution: 'Implemented a high-volume recruiting system paired with leadership development and onboarding improvements.',
    results: [{
      label: 'Final Interviews',
      value: 86,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 57,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 66.3,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Solar',
    companyProfile: 'Community Solar organization in low-density markets',
    challenge: 'Hard-to-reach candidate pools, inconsistent regional performance, lack of a dependable local recruiting strategy.',
    solution: 'Rolled out region-specific recruiting campaigns optimized for rural density, supported by adapted messaging and efficiency modeling.',
    results: [{
      label: 'Monthly Interviews',
      value: 50,
      icon: 'users' as const
    }, {
      label: 'Monthly Hires',
      value: 35,
      suffix: '+',
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 70,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Roofing',
    companyProfile: 'Property Restoration company offering ~$50,000 base salary',
    challenge: 'High payroll overhead + high churn + poor ROI on recruiting spend.',
    solution: 'Provided strategic guidance on restructuring compensation for higher performance alignment (shifting from base salary to commission), then rebuilt the recruiting positioning around that new structure.',
    results: [{
      label: 'Final Interviews',
      value: 60,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 33,
      icon: 'users' as const
    }, {
      label: 'Base Overhead',
      value: 0,
      prefix: '$',
      icon: 'dollar' as const
    }],
    status: 'Active Partnership',
    note: '(Note: Compensation decisions are ultimately made by the client.)',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Energy',
    companyProfile: 'Deregulated energy company expanding into new regions',
    challenge: '$6,000+/month previously spent with another recruiter without results. No hires. No feedback loop or refinement system.',
    solution: 'Installed targeted recruiting campaigns, quality filtering, and leadership support systems in alignment with expansion goals.',
    results: [{
      label: 'Monthly Interviews',
      value: 20,
      icon: 'users' as const
    }, {
      label: 'Monthly Hires',
      value: 15,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 75,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Remodeling',
    companyProfile: 'Growing remodeling company replacing internal recruiter',
    challenge: 'Low output, heavy leadership involvement, poor early retention, limited confidence in scaling.',
    solution: 'Replaced internal ad-hoc recruiting with a fully systemized approach, paired with leadership guidance.',
    results: [{
      label: 'Final Interviews',
      value: 80,
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 59,
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 73.8,
      suffix: '%',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }, {
    industry: 'Roofing',
    companyProfile: 'Nationwide, 10-market operator (PE-backed)',
    challenge: 'Inconsistent internal recruiting, high inefficiency, pressure from ownership to scale predictably.',
    solution: 'Ran multiple synchronized recruiting campaigns with optimization cycles and strategic budget realignment.',
    results: [{
      label: 'Final Interviews',
      value: 200,
      suffix: '+',
      icon: 'users' as const
    }, {
      label: 'New Hires',
      value: 100,
      suffix: '+',
      icon: 'users' as const
    }, {
      label: 'Interview-to-Hire Rate',
      value: 50,
      suffix: '%+',
      icon: 'trending' as const
    }],
    status: 'Active Partnership',
    pdfUrl: CASE_STUDIES_PDF
  }];
  const totalCases = caseStudies.length;
  const cardsPerSet = 2;
  const handleNext = () => {
    if (isTransitioning) return;
    setSlideDirection(-100);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSet(prev => (prev + cardsPerSet) % totalCases);
      setSlideDirection(0);
      setIsTransitioning(false);
    }, 600);
  };
  const handlePrevious = () => {
    if (isTransitioning) return;
    setSlideDirection(100);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSet(prev => (prev - cardsPerSet + totalCases) % totalCases);
      setSlideDirection(0);
      setIsTransitioning(false);
    }, 600);
  };
  const getVisibleCards = () => {
    const cards = [];
    for (let i = 0; i < cardsPerSet; i++) {
      const index = (currentSet + i) % totalCases;
      cards.push({
        ...caseStudies[index],
        key: `${currentSet}-${i}`
      });
    }
    return cards;
  };
  const handleReadMore = (pdfUrl: string, industry: string, companyProfile: string) => {
    setSelectedPDF({
      url: pdfUrl,
      title: `${industry} Case Study – ${companyProfile}`
    });
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <>
      <SEO title="Case Studies | Real D2D Recruiting Results | HIRE" description="See real results from D2D companies using HIRE. 4,000+ hires, 70%+ interview-to-hire rates. Windows, roofing, solar, telecom, and more." canonical="/case-studies" ogTitle="D2D Recruiting Case Studies | The HIRE Advantage" ogDescription="Real companies. Real results. See how D2D companies are scaling their sales teams with the HIRE Advantage system." keywords="d2d recruiting case studies, door to door sales results, sales recruiting success stories, d2d hiring results" data-id="element-1348" />

      <BreadcrumbSchema items={[{
      name: 'Home',
      url: '/'
    }, {
      name: 'Case Studies',
      url: '/case-studies'
    }]} data-id="element-1349" />

      <div className="pt-20" data-id="element-1350">
        {/* Hero */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16 bg-gradient-to-br from-gray-50 to-white" data-id="element-1351">
          <div className="container-custom" data-id="element-1352">
            <motion.div className="max-w-4xl mx-auto text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1353">
              <motion.div className="inline-block bg-slate/10 text-slate px-4 py-2 rounded text-sm font-semibold mb-6" variants={itemVariants} data-id="element-1354">
                Case Studies & Testimonials
              </motion.div>
              <motion.h1 className="mb-6" variants={itemVariants} data-id="element-1355">
                Real Companies. Real Results.
              </motion.h1>
              <motion.p className="text-body-large leading-relaxed mb-4" variants={itemVariants} data-id="element-1356">
                Every company is different, but the goal is the same: Replace
                guesswork with a system that works.
              </motion.p>

              {/* SEO-friendly summary stats visible in HTML */}
              <motion.div className="grid grid-cols-3 gap-4 mt-8 max-w-2xl mx-auto" variants={itemVariants} data-id="element-1357">
                <div className="text-center" data-id="element-1358">
                  <div className="text-2xl font-bold text-mint" data-id="element-1359">4,000+</div>
                  <div className="text-sm text-gray-600" data-id="element-1360">Total Hires</div>
                </div>
                <div className="text-center" data-id="element-1361">
                  <div className="text-2xl font-bold text-mint" data-id="element-1362">70%+</div>
                  <div className="text-sm text-gray-600" data-id="element-1363">
                    Avg Interview-to-Hire
                  </div>
                </div>
                <div className="text-center" data-id="element-1364">
                  <div className="text-2xl font-bold text-mint" data-id="element-1365">10+</div>
                  <div className="text-sm text-gray-600" data-id="element-1366">Industries Served</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Case Studies Carousel - 2 at a time */}
        <section className="section-padding bg-white" data-id="element-1367">
          <div className="container-custom" data-id="element-1368">
            <motion.div className="max-w-7xl mx-auto relative" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} data-id="element-1369">
              <button onClick={handlePrevious} disabled={isTransitioning} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-mint hover:bg-mint-dark text-deep-blue p-4 rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed -ml-6 hidden lg:flex items-center justify-center" aria-label="Previous set" data-id="element-1370">
                <ChevronLeftIcon className="h-6 w-6" data-id="element-1371" />
              </button>
              <button onClick={handleNext} disabled={isTransitioning} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-mint hover:bg-mint-dark text-deep-blue p-4 rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed -mr-6 hidden lg:flex items-center justify-center" aria-label="Next set" data-id="element-1372">
                <ChevronRightIcon className="h-6 w-6" data-id="element-1373" />
              </button>

              <div className="overflow-hidden" data-id="element-1374">
                <div className="grid md:grid-cols-2 gap-8 transition-transform duration-600 ease-in-out" style={{
                transform: `translateX(${slideDirection}%)`
              }} data-id="element-1375">
                  {getVisibleCards().map(study => <DetailedCaseStudyCard key={study.key} {...study} delay={0} onReadMore={() => handleReadMore(study.pdfUrl, study.industry, study.companyProfile)} data-id="element-1376" />)}
                </div>
              </div>

              <div className="flex justify-center gap-4 mt-8 lg:hidden" data-id="element-1377">
                <button onClick={handlePrevious} disabled={isTransitioning} className="bg-mint hover:bg-mint-dark text-deep-blue p-3 rounded-full shadow-lg transition-all disabled:opacity-50" aria-label="Previous" data-id="element-1378">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-1379" />
                </button>
                <button onClick={handleNext} disabled={isTransitioning} className="bg-mint hover:bg-mint-dark text-deep-blue p-3 rounded-full shadow-lg transition-all disabled:opacity-50" aria-label="Next" data-id="element-1380">
                  <ChevronRightIcon className="h-5 w-5" data-id="element-1381" />
                </button>
              </div>

              <div className="flex justify-center items-center gap-3 mt-8" data-id="element-1382">
                <span className="text-sm" data-id="element-1383">
                  Showing {currentSet + 1} –{' '}
                  {Math.min(currentSet + cardsPerSet, totalCases)} of{' '}
                  {totalCases}
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        {selectedPDF && <PDFModal isOpen={!!selectedPDF} onClose={() => setSelectedPDF(null)} pdfUrl={selectedPDF.url} title={selectedPDF.title} data-id="element-1384" />}

        <VideoTestimonials data-id="element-1385" />
        <ClientLogos data-id="element-1386" />
        <ResultsSummary data-id="element-1387" />

        <section className="section-padding bg-white" data-id="element-1388">
          <div className="container-custom" data-id="element-1389">
            <motion.div className="max-w-3xl mx-auto text-center" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            ease: [0.25, 0.1, 0.25, 1]
          }} data-id="element-1390">
              <h2 className="mb-6" data-id="element-1391">Want Results Like These?</h2>
              <p className="text-body-large mb-8 leading-relaxed" data-id="element-1392">
                These companies started just like you – overwhelmed and stuck.
                The difference was installing a system.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1393">
                <a href="/book-call" className="inline-block bg-mint hover:bg-mint-dark text-deep-blue font-bold px-8 py-4 rounded-lg transition-colors shadow-lg" data-id="element-1394">
                  Schedule Your Strategy Call →
                </a>
                <a href="/how-it-works" className="inline-block border-2 border-deep-blue text-deep-blue hover:bg-deep-blue hover:text-white font-bold px-8 py-4 rounded-lg transition-colors" data-id="element-1395">
                  See How the System Works
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>;
}